import type { ActivityItem, InventoryInput, InventoryItem, ProductHistoryResult, ProductLookup, RecommendationItem, ShoppingItem } from "./types";
import { addDays, todayInSydney } from "./lib/expiry";
import { buildRecommendations } from "./lib/recommendations";
import { isStockReminderDue } from "./lib/stock";

const API_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, "") ?? "";
export const isDemoMode = import.meta.env.VITE_DEMO_MODE !== "false" || !API_URL;

type TokenProvider = () => string | null | Promise<string | null>;

let tokenProvider: TokenProvider = () => null;
export function setTokenProvider(provider: TokenProvider) {
  tokenProvider = provider;
}

type DemoState = {
  inventory: InventoryItem[];
  shopping: ShoppingItem[];
  activity: DemoActivityItem[];
};

type DemoActivityItem = ActivityItem & {
  revertMode?: "restore" | "remove";
  beforeState?: InventoryItem;
};

const STORAGE_KEY = "sapk-grocery-demo-v1";

function nowIso() {
  return new Date().toISOString();
}

function id() {
  return crypto.randomUUID();
}

function seedDemoState(): DemoState {
  const today = todayInSydney();
  const timestamp = nowIso();
  const items: InventoryItem[] = [
    {
      id: id(), productName: "Greek yoghurt", brand: "Farmhouse", category: "Dairy", quantity: 1,
      unit: "tub", location: "Fridge", expiryDate: addDays(today, 2), expiryType: "use-by",
      minimumQuantity: 1, isOpen: true, status: "ACTIVE", version: 1, createdBy: "Sap", updatedBy: "Sap",
      createdAt: timestamp, updatedAt: timestamp,
    },
    {
      id: id(), productName: "Baby spinach", category: "Produce", quantity: 1,
      unit: "bag", location: "Fridge", expiryDate: addDays(today, 1), expiryType: "use-by",
      minimumQuantity: 1, isOpen: false, status: "ACTIVE", version: 1, createdBy: "Rohit", updatedBy: "Rohit",
      createdAt: timestamp, updatedAt: timestamp,
    },
    {
      id: id(), productName: "Basmati rice", brand: "India Gate", category: "Grains", quantity: 2,
      unit: "kg", location: "Pantry", expiryDate: addDays(today, 180), expiryType: "best-before",
      minimumQuantity: 1, isOpen: false, status: "ACTIVE", version: 1, createdBy: "Nakul", updatedBy: "Nakul",
      createdAt: timestamp, updatedAt: timestamp,
    },
    {
      id: id(), productName: "Frozen peas", category: "Frozen", quantity: 0.5,
      unit: "bag", location: "Freezer", expiryDate: addDays(today, 70), expiryType: "best-before",
      minimumQuantity: 1, isOpen: true, status: "ACTIVE", version: 1, createdBy: "Sap", updatedBy: "Sap",
      createdAt: timestamp, updatedAt: timestamp,
    },
  ];
  return {
    inventory: items,
    shopping: [
      { id: id(), name: "Bananas", quantity: 6, unit: "pcs", checked: false, version: 1, createdBy: "Rohit", createdAt: timestamp, updatedAt: timestamp },
      { id: id(), name: "Oat milk", quantity: 2, unit: "cartons", checked: false, version: 1, createdBy: "Sap", createdAt: timestamp, updatedAt: timestamp },
    ],
    activity: [
      { id: id(), action: "added", entityName: "Baby spinach", detail: "1 bag · Fridge", performedBy: "Rohit", createdAt: timestamp },
      { id: id(), action: "updated", entityName: "Basmati rice", detail: "Quantity set to 2 kg", performedBy: "Nakul", createdAt: timestamp },
    ],
  };
}

function loadDemo(): DemoState {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw) return JSON.parse(raw) as DemoState;
  const state = seedDemoState();
  saveDemo(state);
  return state;
}

function saveDemo(state: DemoState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function demoUser() {
  return localStorage.getItem("sapk-grocery-demo-user") ?? "Sap";
}

function recordActivity(
  state: DemoState,
  action: string,
  entityName: string,
  detail?: string,
  options: { targetId?: string; beforeState?: InventoryItem; afterState?: InventoryItem; revertMode?: "restore" | "remove" } = {},
) {
  state.activity.unshift({
    id: id(), action, entityName, detail, performedBy: demoUser(), createdAt: nowIso(),
    ...(options.targetId ? { targetType: "inventory" as const, targetId: options.targetId } : {}),
    ...(options.afterState ? { itemSnapshot: options.afterState } : {}),
    ...(options.beforeState ? { beforeState: options.beforeState } : {}),
    ...(options.revertMode ? { revertMode: options.revertMode } : {}),
  });
  state.activity = state.activity.slice(0, 100);
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await tokenProvider();
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const message = await response.json().catch(() => ({ error: "The request could not be completed." }));
    throw new Error(message.error ?? "The request could not be completed.");
  }
  return response.status === 204 ? (undefined as T) : response.json() as Promise<T>;
}

export async function listInventory(): Promise<InventoryItem[]> {
  if (!isDemoMode) {
    const items = await request<InventoryItem[]>("/inventory");
    return items.map((item) => ({ ...item, isOpen: item.isOpen ?? false }));
  }
  return loadDemo().inventory.filter((item) => item.status === "ACTIVE");
}

export async function listRecommendations(): Promise<RecommendationItem[]> {
  if (!isDemoMode) return request<RecommendationItem[]>("/recommendations");
  return buildRecommendations(loadDemo().inventory) as RecommendationItem[];
}

export async function createInventory(input: InventoryInput, restockedFromId?: string): Promise<InventoryItem> {
  if (!isDemoMode) return request<InventoryItem>("/inventory", { method: "POST", body: JSON.stringify({ ...input, restockedFromId }) });
  const state = loadDemo();
  const timestamp = nowIso();
  const sourceIndex = state.inventory.findIndex((existing) => existing.id === restockedFromId);
  const source = sourceIndex >= 0 ? state.inventory[sourceIndex] : undefined;
  const previousActiveQuantity = source?.status === "ACTIVE" ? source.quantity : 0;
  const item: InventoryItem = {
    ...input, id: id(), status: "ACTIVE",
    recommendationDismissed: Boolean(source && isStockReminderDue(input.quantity + previousActiveQuantity, input.minimumQuantity)),
    version: 1, createdBy: demoUser(), updatedBy: demoUser(),
    createdAt: timestamp, updatedAt: timestamp,
  };
  state.inventory.unshift(item);
  if (sourceIndex >= 0) {
    const shiftedSourceIndex = sourceIndex + 1;
    state.inventory[shiftedSourceIndex] = {
      ...state.inventory[shiftedSourceIndex], recommendationDismissed: true,
      version: state.inventory[shiftedSourceIndex].version + 1, updatedBy: demoUser(), updatedAt: timestamp,
    };
  }
  recordActivity(state, "added", item.productName, `${item.quantity} ${item.unit} · ${item.location}`, {
    targetId: item.id, afterState: item, revertMode: "remove",
  });
  saveDemo(state);
  return item;
}

export async function updateInventory(itemId: string, changes: Partial<InventoryItem> & { usedOne?: boolean }): Promise<InventoryItem> {
  if (!isDemoMode) return request<InventoryItem>(`/inventory/${itemId}`, { method: "PATCH", body: JSON.stringify(changes) });
  const state = loadDemo();
  const index = state.inventory.findIndex((item) => item.id === itemId);
  if (index < 0) throw new Error("Item not found.");
  const beforeState = { ...state.inventory[index] };
  const { usedOne, ...inventoryChanges } = changes;
  const updated = { ...state.inventory[index], ...inventoryChanges, version: state.inventory[index].version + 1, updatedBy: demoUser(), updatedAt: nowIso() };
  if (updated.status === "ACTIVE" && !isStockReminderDue(updated.quantity, updated.minimumQuantity)) {
    updated.recommendationDismissed = false;
  }
  state.inventory[index] = updated;
  recordActivity(
    state,
    changes.recommendationDismissed === true ? "hid recommendation for" : usedOne || changes.status === "CONSUMED" ? "used" : changes.status === "DISCARDED" ? "discarded" : "updated",
    updated.productName,
    usedOne ? `1 ${updated.unit} used · ${updated.quantity} remaining` : undefined,
    { targetId: itemId, beforeState, afterState: updated, revertMode: "restore" },
  );
  saveDemo(state);
  return updated;
}

export async function removeInventory(itemId: string): Promise<void> {
  await updateInventory(itemId, { status: "DELETED" });
}

export async function listShopping(): Promise<ShoppingItem[]> {
  if (!isDemoMode) return request<ShoppingItem[]>("/shopping");
  return loadDemo().shopping;
}

export async function createShopping(name: string, quantity: number, unit: string, sourceInventoryId?: string): Promise<ShoppingItem> {
  if (!isDemoMode) return request<ShoppingItem>("/shopping", { method: "POST", body: JSON.stringify({ name, quantity, unit, sourceInventoryId }) });
  const state = loadDemo();
  const timestamp = nowIso();
  const item: ShoppingItem = { id: id(), name, quantity, unit, sourceInventoryId, checked: false, version: 1, createdBy: demoUser(), createdAt: timestamp, updatedAt: timestamp };
  state.shopping.push(item);
  recordActivity(state, "listed", item.name, `${quantity} ${unit}`);
  saveDemo(state);
  return item;
}

export async function updateShopping(itemId: string, changes: Partial<ShoppingItem>): Promise<ShoppingItem> {
  if (!isDemoMode) return request<ShoppingItem>(`/shopping/${itemId}`, { method: "PATCH", body: JSON.stringify(changes) });
  const state = loadDemo();
  const index = state.shopping.findIndex((item) => item.id === itemId);
  if (index < 0) throw new Error("List item not found.");
  const updated = { ...state.shopping[index], ...changes, version: state.shopping[index].version + 1, updatedAt: nowIso() };
  state.shopping[index] = updated;
  saveDemo(state);
  return updated;
}

export async function removeShopping(itemId: string): Promise<void> {
  if (!isDemoMode) return request<void>(`/shopping/${itemId}`, { method: "DELETE" });
  const state = loadDemo();
  state.shopping = state.shopping.filter((item) => item.id !== itemId);
  saveDemo(state);
}

export async function listActivity(): Promise<ActivityItem[]> {
  if (!isDemoMode) return request<ActivityItem[]>("/activity");
  const state = loadDemo();
  const seenTargets = new Set<string>();
  return state.activity.map(({ beforeState: _beforeState, revertMode, ...item }) => {
    const isLatest = Boolean(item.targetId) && !seenTargets.has(item.targetId!);
    if (item.targetId) seenTargets.add(item.targetId);
    const legacySnapshot = item.action === "used" && !item.itemSnapshot
      ? [...state.inventory].sort((left, right) => right.updatedAt.localeCompare(left.updatedAt)).find((candidate) =>
        candidate.id === item.targetId || candidate.productName.toLowerCase() === item.entityName.toLowerCase(),
      )
      : undefined;
    return {
      ...item,
      ...(legacySnapshot ? { itemSnapshot: legacySnapshot } : {}),
      canUndo: Boolean(isLatest && revertMode && !item.undoneAt),
    };
  });
}

export async function undoActivity(activityId: string): Promise<InventoryItem> {
  if (!isDemoMode) return request<InventoryItem>(`/activity/${activityId}/undo`, { method: "POST" });
  const state = loadDemo();
  const activityIndex = state.activity.findIndex((item) => item.id === activityId);
  if (activityIndex < 0) throw new Error("Activity not found.");
  const activity = state.activity[activityIndex];
  if (activity.undoneAt) throw new Error("This activity has already been undone.");
  if (!activity.targetId || !activity.revertMode) throw new Error("This activity cannot be undone.");
  if (state.activity.find((item) => item.targetId === activity.targetId)?.id !== activity.id) {
    throw new Error("Only the latest action for this item can be undone.");
  }
  const inventoryIndex = state.inventory.findIndex((item) => item.id === activity.targetId);
  if (inventoryIndex < 0) throw new Error("Pantry item not found.");
  const current = state.inventory[inventoryIndex];
  const timestamp = nowIso();
  const restored: InventoryItem = activity.revertMode === "restore" && activity.beforeState
    ? { ...current, ...activity.beforeState, id: current.id, version: current.version + 1, updatedBy: demoUser(), updatedAt: timestamp }
    : { ...current, status: "DELETED", version: current.version + 1, updatedBy: demoUser(), updatedAt: timestamp };
  state.inventory[inventoryIndex] = restored;
  state.activity[activityIndex] = { ...activity, undoneAt: timestamp, undoneBy: demoUser() };
  recordActivity(
    state, "restored", activity.entityName,
    `Undid ${activity.action} by ${activity.performedBy}`,
    { targetId: current.id, beforeState: current, afterState: restored },
  );
  saveDemo(state);
  return restored;
}

export async function lookupBarcode(barcode: string): Promise<ProductLookup> {
  if (!isDemoMode) return request<ProductLookup>(`/products/${encodeURIComponent(barcode)}`);
  const samples: Record<string, ProductLookup> = {
    "9300657030534": { barcode, found: true, productName: "Full cream milk", brand: "Dairy Farmers", category: "Dairy", source: "Demo catalogue" },
    "9310072022542": { barcode, found: true, productName: "Chocolate biscuits", brand: "Arnott's", category: "Snacks", source: "Demo catalogue" },
  };
  return samples[barcode] ?? { barcode, found: false };
}

export async function searchProducts(query: string): Promise<ProductHistoryResult[]> {
  const term = query.trim().toLowerCase();
  if (term.length < 2) return [];
  if (!isDemoMode) return request<ProductHistoryResult[]>(`/products/search?q=${encodeURIComponent(term)}`);

  const seen = new Set<string>();
  return [...loadDemo().inventory]
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))
    .filter((item) => `${item.productName} ${item.brand ?? ""} ${item.barcode ?? ""} ${item.category}`.toLowerCase().includes(term))
    .filter((item) => {
      const key = item.barcode || `${item.productName.toLowerCase()}|${(item.brand ?? "").toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 8);
}

export function exportDemoData() {
  return loadDemo();
}
